import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Users,
  ClipboardList,
  Phone,
  Mail,
  Loader2,
  Copy,
  ExternalLink,
  CheckCircle2,
  Trash2,
  Link2,
  Filter,
  MessageSquare,
  ShieldCheck,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { formatDateES, studentShortLabel } from "@/lib/utils-app";

const STATUS_BADGE = {
  pending_review: { label: "Pendiente revisión", cls: "bg-amber-100 text-amber-800" },
  reviewed: { label: "Revisado", cls: "bg-emerald-100 text-emerald-800" },
  archived: { label: "Archivado", cls: "bg-neutral-100 text-neutral-600" },
};

function KaiAgentBadge() {
  return (
    <div className="inline-flex items-center gap-2 rounded-full bg-orange-50 border border-orange-200 pl-1 pr-3 py-1">
      <img src="/agents/kai.png" alt="Kai" className="h-7 w-7 rounded-full object-cover" style={{ objectPosition: "50% 20%" }} />
      <div className="leading-tight">
        <div className="text-xs font-bold text-orange-800">Kai</div>
        <div className="text-[10px] text-orange-600">Cuestionarios</div>
      </div>
    </div>
  );
}

function AgentStatusStrip() {
  const agents = [
    { key: "kai", name: "Kai", role: "Cuestionarios", active: true },
    { key: "ana", name: "Ana", role: "Comercial", active: false },
    { key: "dakota", name: "Dakota", role: "Coordinador", active: false },
    { key: "cyrus", name: "Cyrus", role: "Product Manager", active: false },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="kai-agent-strip">
      {agents.map((a) => (
        <div
          key={a.key}
          data-testid={`kai-agent-${a.key}`}
          className={
            "rounded-2xl border p-3 flex items-center gap-2 " +
            (a.active ? "border-orange-200 bg-orange-50" : "border-neutral-200 bg-neutral-50/50 opacity-70")
          }
        >
          <img
            src={a.key === "cyrus" ? "/agents/cyrus.jpg" : `/agents/${a.key}.png`}
            alt={a.name}
            className="h-11 w-11 rounded-full object-cover ring-2 ring-white shadow-sm"
            style={{ objectPosition: "50% 20%" }}
          />
          <div className="min-w-0">
            <div className="font-bold text-sm truncate">{a.name}</div>
            <div className="text-[11px] text-neutral-500 truncate">{a.role}</div>
            <div className={"text-[10px] font-semibold mt-0.5 " + (a.active ? "text-emerald-700" : "text-neutral-400")}>
              {a.active ? "● Activo" : "○ Inactivo"}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function StudentLinker({ questionnaire, onLinked }) {
  const [students, setStudents] = useState([]);
  const [query, setQuery] = useState(questionnaire.fullName || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/students");
        setStudents(data || []);
      } catch (e) { /* silent */ }
    })();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return students.slice(0, 20);
    return students.filter((s) =>
      (s.name || "").toLowerCase().includes(q) ||
      (s.phone || "").includes(q) ||
      (s.email || "").toLowerCase().includes(q)
    ).slice(0, 20);
  }, [students, query]);

  const link = async (sid) => {
    setSaving(true);
    try {
      await api.patch(`/kai/questionnaires/${questionnaire.id}`, { linked_student_id: sid });
      toast.success("Vinculado al alumno");
      onLinked?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <input
        type="text"
        placeholder="Buscar alumno por nombre, teléfono o email…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        className="input-base w-full mb-2"
        data-testid="kai-link-search"
      />
      <div className="max-h-52 overflow-y-auto rounded-xl border border-neutral-200 divide-y divide-neutral-100">
        {filtered.length === 0 ? (
          <div className="px-3 py-3 text-sm text-neutral-500">Sin coincidencias.</div>
        ) : filtered.map((s) => (
          <button
            key={s.id}
            type="button"
            disabled={saving}
            onClick={() => link(s.id)}
            data-testid={`kai-link-option-${s.id}`}
            className="w-full text-left px-3 py-2 hover:bg-orange-50 flex items-center justify-between gap-2"
          >
            <div className="min-w-0">
              <div className="font-semibold text-sm truncate">{studentShortLabel(s) || s.name}</div>
              <div className="text-xs text-neutral-500 truncate">{s.phone} · {s.email || "sin email"}</div>
            </div>
            <Link2 className="h-4 w-4 text-orange-500 shrink-0" />
          </button>
        ))}
      </div>
    </div>
  );
}

function QuestionnaireDetail({ q, onClose, onChanged }) {
  const [showLinker, setShowLinker] = useState(false);

  const markReviewed = async () => {
    try {
      await api.patch(`/kai/questionnaires/${q.id}`, { status: "reviewed" });
      toast.success("Marcado como revisado");
      onChanged?.();
      onClose?.();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const remove = async () => {
    if (!window.confirm(`¿Eliminar el cuestionario de ${q.fullName}?`)) return;
    try {
      await api.delete(`/kai/questionnaires/${q.id}`);
      toast.success("Cuestionario eliminado");
      onChanged?.();
      onClose?.();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const copySummary = () => {
    navigator.clipboard.writeText(q.summary || "");
    toast.success("Resumen copiado al portapapeles");
  };

  const badge = STATUS_BADGE[q.status] || STATUS_BADGE.pending_review;

  return (
    <div className="space-y-5" data-testid="kai-detail">
      {/* Header */}
      <div className="flex items-start gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <h3 className="text-xl font-bold truncate">{q.fullName}</h3>
          <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-neutral-600">
            <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{q.phone}</span>
            {q.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{q.email}</span>}
            <span>Recibido {formatDateES(q.createdAt?.slice(0, 10))}</span>
          </div>
        </div>
        <span className={"inline-flex items-center rounded-full text-[11px] font-semibold px-2.5 py-1 " + badge.cls}>
          {badge.label}
        </span>
      </div>

      {/* Resumen */}
      <section>
        <div className="flex items-center justify-between gap-2 mb-2">
          <h4 className="text-sm font-bold uppercase tracking-wider text-neutral-500">Resumen automático</h4>
          <button type="button" onClick={copySummary} className="btn-ghost !h-8 !px-2 !text-xs border border-neutral-200" data-testid="kai-copy-summary">
            <Copy className="h-3 w-3" /> Copiar
          </button>
        </div>
        <pre className="rounded-xl bg-neutral-50 border border-neutral-200 px-4 py-3 text-sm text-neutral-800 whitespace-pre-wrap font-sans">
{q.summary}
        </pre>
      </section>

      {/* Detalles */}
      <section>
        <h4 className="text-sm font-bold uppercase tracking-wider text-neutral-500 mb-2">Todos los campos</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
          {[
            ["Trasladado", q.transferStudent ? "Sí" : "No"],
            ["Autoescuela origen", q.previousDrivingSchool || "—"],
            ["Teórico aprobado", q.theoryApproved ? "Sí" : "No"],
            ["Intentos teórico", q.theoryAttempts],
            ["Aprobado en intento", q.approvedTheoryAttempt || "—"],
            ["Último resultado", q.lastTheoryResult || "—"],
            ["Tasas teórico pendientes", { si: "Sí", no: "No", no_lo_se: "No lo sabe" }[q.pendingTheoryFees] || "—"],
            ["Prácticos otra autoescuela", q.practicalAttemptsOtherSchool],
            ["Prácticos Natur", q.practicalAttemptsNatur],
            ["Tasas pagadas", q.feesPaid],
          ].map(([k, v]) => (
            <div key={k} className="flex justify-between gap-2 py-1 border-b border-neutral-100 last:border-0">
              <span className="text-neutral-500">{k}</span>
              <span className="font-medium text-neutral-800 text-right">{v}</span>
            </div>
          ))}
        </div>
        {q.notes && (
          <div className="mt-3 rounded-xl bg-neutral-50 border border-neutral-200 p-3 text-sm">
            <div className="text-xs font-semibold text-neutral-500 mb-1 flex items-center gap-1">
              <MessageSquare className="h-3 w-3" /> Observaciones del alumno
            </div>
            <div className="whitespace-pre-wrap">{q.notes}</div>
          </div>
        )}
      </section>

      {/* Vinculación */}
      <section>
        <h4 className="text-sm font-bold uppercase tracking-wider text-neutral-500 mb-2">Alumno vinculado</h4>
        {q.linked_student ? (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm flex items-center justify-between">
            <span className="flex items-center gap-2"><Link2 className="h-4 w-4 text-emerald-600" /> <strong>{studentShortLabel(q.linked_student) || q.linked_student.name}</strong></span>
            <button type="button" onClick={() => setShowLinker(true)} className="text-xs underline text-emerald-800" data-testid="kai-relink">Cambiar</button>
          </div>
        ) : (
          <button type="button" onClick={() => setShowLinker(true)} className="btn-secondary !h-9" data-testid="kai-link-btn">
            <Link2 className="h-4 w-4" /> Vincular a un alumno
          </button>
        )}
        {showLinker && (
          <div className="mt-3">
            <StudentLinker
              questionnaire={q}
              onLinked={() => { setShowLinker(false); onChanged?.(); }}
            />
          </div>
        )}
      </section>

      {/* Acciones */}
      <DialogFooter className="gap-2 flex-wrap">
        <button type="button" onClick={remove} className="btn-ghost !h-9 border border-red-200 text-red-600 hover:bg-red-50" data-testid="kai-delete">
          <Trash2 className="h-4 w-4" /> Eliminar
        </button>
        {q.status !== "reviewed" && (
          <button type="button" onClick={markReviewed} className="btn-primary !h-9" data-testid="kai-mark-reviewed">
            <CheckCircle2 className="h-4 w-4" /> Marcar revisado
          </button>
        )}
      </DialogFooter>
    </div>
  );
}

export default function KaiQuestionnairesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("pending_review");
  const [openId, setOpenId] = useState(null);
  const [openDetail, setOpenDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = statusFilter ? `/kai/questionnaires?status=${statusFilter}` : "/kai/questionnaires";
      const { data } = await api.get(url);
      setItems(data || []);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!openId) { setOpenDetail(null); return; }
    (async () => {
      try {
        const { data } = await api.get(`/kai/questionnaires/${openId}`);
        setOpenDetail(data);
      } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); setOpenId(null); }
    })();
  }, [openId]);

  const [genLink, setGenLink] = useState(null); // V1.54 · enlace seguro generado
  const [genBusy, setGenBusy] = useState(false);
  const generateSecureLink = async () => {
    setGenBusy(true);
    try {
      const { data } = await api.post("/kai-questionnaire-link", { student_id: "" });
      setGenLink(data);
      navigator.clipboard.writeText(data.portal_url);
      toast.success(`Enlace único generado y copiado · caduca en ${data.expires_hours} h`);
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
    finally { setGenBusy(false); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="text-xl sm:text-2xl font-bold">Cuestionarios de alumnos</h2>
            <KaiAgentBadge />
          </div>
          <p className="text-sm text-neutral-500 mt-1">
            Enlaces únicos de un solo uso con caducidad — nunca se repiten. Genera uno por alumno y compártelo por WhatsApp.
          </p>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <button type="button" onClick={generateSecureLink} disabled={genBusy} className="btn-primary !h-9 !text-xs" data-testid="kai-generate-secure-link">
            <ShieldCheck className="h-3.5 w-3.5" /> {genBusy ? "Generando…" : "Generar enlace seguro"}
          </button>
          {genLink && (
            <div className="flex items-center gap-1.5">
              <span className="rounded-lg border border-neutral-200 bg-neutral-50 px-2 py-1 text-[10px] font-mono max-w-[240px] truncate" data-testid="kai-secure-link-url">
                {genLink.portal_url.replace(/^https?:\/\//, "")}
              </span>
              <button type="button" onClick={() => { navigator.clipboard.writeText(genLink.portal_url); toast.success("Copiado"); }} className="btn-secondary !h-7 !text-[10px] !px-2" data-testid="kai-copy-link">
                <Copy className="h-3 w-3" /> Copiar
              </button>
              <span className="text-[10px] text-neutral-400">un solo uso · {genLink.expires_hours} h</span>
            </div>
          )}
        </div>
      </div>

      <AgentStatusStrip />

      {/* Filter */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="kai-filters">
        <Filter className="h-4 w-4 text-neutral-400" />
        {[
          { v: "pending_review", label: "Pendientes" },
          { v: "reviewed", label: "Revisados" },
          { v: "archived", label: "Archivados" },
          { v: "", label: "Todos" },
        ].map((o) => (
          <button
            key={o.v || "all"}
            type="button"
            data-testid={`kai-filter-${o.v || "all"}`}
            onClick={() => setStatusFilter(o.v)}
            className={
              "h-8 px-3 rounded-full text-xs font-semibold border transition-colors " +
              (statusFilter === o.v
                ? "bg-orange-500 border-orange-500 text-white"
                : "bg-white border-neutral-200 text-neutral-600 hover:border-orange-300")
            }
          >
            {o.label}
          </button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div className="text-sm text-neutral-500 py-4 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Cargando…</div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-neutral-200 bg-white/50 p-10 text-center" data-testid="kai-empty">
          <ClipboardList className="h-8 w-8 mx-auto text-neutral-300" />
          <h3 className="mt-3 font-semibold text-neutral-800">Sin cuestionarios</h3>
          <p className="text-sm text-neutral-500 mt-1">Comparte el enlace público con tus alumnos para empezar a recibir respuestas.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {items.map((q) => {
            const b = STATUS_BADGE[q.status] || STATUS_BADGE.pending_review;
            return (
              <button
                key={q.id}
                type="button"
                onClick={() => setOpenId(q.id)}
                data-testid={`kai-row-${q.id}`}
                className="text-left rounded-2xl border border-neutral-200 bg-white p-4 hover:border-orange-300 hover:shadow-sm transition-all"
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h3 className="font-bold truncate">{q.fullName}</h3>
                  <span className={"shrink-0 inline-flex items-center rounded-full text-[10px] font-semibold px-2 py-0.5 " + b.cls}>{b.label}</span>
                </div>
                <div className="text-xs text-neutral-500 flex items-center gap-2 flex-wrap mb-2">
                  <Phone className="h-3 w-3" />{q.phone}
                  {q.transferStudent && <span className="rounded-full bg-blue-100 text-blue-800 text-[10px] font-semibold px-1.5 py-0.5">Trasladado</span>}
                  {q.theoryApproved && <span className="rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-semibold px-1.5 py-0.5">Teórico ✓</span>}
                </div>
                <div className="text-xs text-neutral-600 flex items-center gap-3">
                  <span>Prácticos otros: <strong>{q.practicalAttemptsOtherSchool}</strong></span>
                  <span>· Natur: <strong>{q.practicalAttemptsNatur}</strong></span>
                  <span>· Tasas: <strong>{q.feesPaid}</strong></span>
                </div>
                <div className="text-[10px] text-neutral-400 mt-2">{formatDateES(q.createdAt?.slice(0, 10))}</div>
              </button>
            );
          })}
        </div>
      )}

      {/* Detail dialog */}
      <Dialog open={!!openId} onOpenChange={(o) => !o && setOpenId(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="kai-detail-dialog">
          <DialogHeader>
            <DialogTitle>Cuestionario Kai</DialogTitle>
            <DialogDescription>
              Revisa los datos, vincula a un alumno existente y márcalo como revisado.
            </DialogDescription>
          </DialogHeader>
          {!openDetail ? (
            <div className="text-sm text-neutral-500 py-4 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Cargando…</div>
          ) : (
            <QuestionnaireDetail q={openDetail} onClose={() => setOpenId(null)} onChanged={load} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
